import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { INQUIRY_INTERESTS } from "@/lib/inquiryOptions";

export default function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", subject: "", message: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const fullMessage = form.interest
        ? `Interest: ${form.interest}\n\n${form.message}`
        : form.message;
      await api.post("/inquiries", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        subject: form.subject || form.interest,
        message: fullMessage,
        inquiry_type: "general",
      });
      toast.success("Message sent! Tammy will reply within 1–2 business days.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="contact-success">
        <CheckCircle2 className="mx-auto text-primary" size={56} strokeWidth={1.4} />
        <h1 className="font-serif text-5xl mt-8">Message received.</h1>
        <p className="text-lg text-foreground/75 mt-6 leading-relaxed">
          Thanks for reaching out. Tammy reads every message herself and will
          respond within 1–2 business days.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="contact-page">
      <div className="grid md:grid-cols-12 gap-12 items-start">
        <div className="md:col-span-5">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">
            Contact
          </span>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 leading-[1.02]">
            Have a question?
          </h1>
          <p className="text-lg text-foreground/80 mt-6 leading-relaxed">
            Whether you're curious about coaching, want to collaborate, or just
            want to say hi — drop a note. Tammy answers everything personally.
          </p>

          <div className="mt-10 space-y-5">
            <Detail icon={Mail} title="Email" value="trichard@kw.com" href="mailto:trichard@kw.com" />
            <Detail icon={Phone} title="Mobile" value="(207) 838-0342" href="tel:+12078380342" />
            <Detail icon={Phone} title="Office" value="(207) 553-2427" href="tel:+12075532427" />
            <Detail icon={MapPin} title="Based in" value="Greater Portland, Maine · Keller Williams" />
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="md:col-span-7 bg-card border border-border rounded-2xl p-8 md:p-10 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name *">
              <Input
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Cooper"
                className="rounded-xl h-11"
                data-testid="contact-name"
                required
              />
            </Field>
            <Field label="Email *">
              <Input
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jane@example.com"
                className="rounded-xl h-11"
                data-testid="contact-email"
                required
              />
            </Field>
          </div>
          <Field label="Phone (optional)">
            <Input
              value={form.phone}
              onChange={update("phone")}
              placeholder="(555) 555-5555"
              className="rounded-xl h-11"
              data-testid="contact-phone"
            />
          </Field>
          <Field label="What are you most interested in?">
            <Select value={form.interest} onValueChange={update("interest")}>
              <SelectTrigger className="rounded-xl h-11" data-testid="contact-interest-trigger">
                <SelectValue placeholder="Select one" />
              </SelectTrigger>
              <SelectContent>
                {INQUIRY_INTERESTS.map((i) => (
                  <SelectItem key={i} value={i}
                    data-testid={`contact-interest-${i.replace(/[^a-zA-Z0-9]/g, '')}`}>{i}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Subject">
            <Input
              value={form.subject}
              onChange={update("subject")}
              placeholder="What's this about?"
              className="rounded-xl h-11"
              data-testid="contact-subject"
            />
          </Field>
          <Field label="Message *">
            <Textarea
              value={form.message}
              onChange={update("message")}
              rows={5}
              placeholder="Tell Tammy what's on your mind..."
              className="rounded-xl"
              data-testid="contact-message"
              required
            />
          </Field>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="w-full rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 text-base"
          >
            {submitting ? "Sending..." : "Send message"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.18em] text-foreground/70 font-medium">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Detail({ icon: Icon, title, value, href }) {
  const content = (
    <>
      <div className="w-10 h-10 rounded-full bg-primary/12 flex items-center justify-center shrink-0">
        <Icon className="text-primary" size={16} strokeWidth={1.7} />
      </div>
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-foreground/60">{title}</p>
        <p className="text-base text-foreground mt-0.5">{value}</p>
      </div>
    </>
  );
  if (href) {
    return (
      <a href={href} className="flex items-start gap-4 hover:text-primary transition">
        {content}
      </a>
    );
  }
  return <div className="flex items-start gap-4">{content}</div>;
}
